export const CONFIRMATION_POLICY_STEPS = [
  { id: 1, label: "Policy basics", shortLabel: "Basics" },
  { id: 2, label: "Confirmation rules", shortLabel: "Rules" },
  { id: 3, label: "Workflow", shortLabel: "Workflow" },
  { id: 4, label: "Communication & letters", shortLabel: "Letters" },
  { id: 5, label: "Checklist mapping", shortLabel: "Checklist" },
  { id: 6, label: "Applicability", shortLabel: "Applicability" },
];

export const TRANSFER_POLICY_STEPS = [
  { id: 1, label: "Policy basics", shortLabel: "Basics" },
  { id: 2, label: "Transfer rules", shortLabel: "Rules" },
  { id: 3, label: "Workflow", shortLabel: "Workflow" },
  { id: 4, label: "Communication & letters", shortLabel: "Letters" },
  { id: 5, label: "Checklist mapping", shortLabel: "Checklist" },
  { id: 6, label: "Applicability", shortLabel: "Applicability" },
];

export const SEPARATION_POLICY_STEPS = [
  { id: 1, label: "Policy basics", shortLabel: "Basics" },
  { id: 2, label: "Separation rules", shortLabel: "Rules" },
  { id: 3, label: "Workflow", shortLabel: "Workflow" },
  { id: 4, label: "Communication & letters", shortLabel: "Letters" },
  { id: 5, label: "Checklist mapping", shortLabel: "Checklist" },
  { id: 6, label: "Applicability", shortLabel: "Applicability" },
];

/**
 * Attendance Policy — shortened wizard (max 5).
 * formStep is the panel id used inside AttendancePolicyComponent.
 * Workflow + Applicability stay last.
 */
export const ATTENDANCE_POLICY_STEPS = [
  { id: 1, label: "Policy basics", shortLabel: "Basics", formStep: 1 },
  { id: 2, label: "Time rules & marking", shortLabel: "Time rules", formStep: 2 },
  { id: 3, label: "Requests & groups", shortLabel: "Requests", formStep: 3 },
  { id: 4, label: "Workflow", shortLabel: "Workflow", formStep: 4 },
  { id: 5, label: "Applicability", shortLabel: "Apply", formStep: 5 },
];

/**
 * Manual Override Attendance path (Auto + Override).
 * Cut-off lives on Applicability with the rest of apply settings.
 */
export const ATTENDANCE_POLICY_OVERRIDE_STEPS = [
  { id: 1, label: "Policy basics", shortLabel: "Basics", formStep: 1 },
  {
    id: 2,
    label: "Manual marking (Override)",
    shortLabel: "Marking",
    formStep: 2,
  },
  {
    id: 3,
    label: "Applicability & save",
    shortLabel: "Apply",
    formStep: 5,
  },
];

export function isAttendancePolicyOverrideMode(policy) {
  return !!(policy?.autoAttendance && policy?.overrideAttendance);
}

export function getAttendancePolicyWizardSteps(policy) {
  return isAttendancePolicyOverrideMode(policy)
    ? ATTENDANCE_POLICY_OVERRIDE_STEPS
    : ATTENDANCE_POLICY_STEPS;
}

/** Wizard display id for current form panel. */
export function getAttendancePolicyWizardCurrentId(policy, formStep) {
  const steps = getAttendancePolicyWizardSteps(policy);
  const hit = steps.find((s) => s.formStep === formStep);
  return hit?.id ?? 1;
}

export function mapAttendancePolicyWizardClickToFormStep(policy, wizardStepId) {
  const steps = getAttendancePolicyWizardSteps(policy);
  const hit = steps.find((s) => s.id === wizardStepId);
  return hit?.formStep ?? 1;
}

export function getNextAttendancePolicyFormStep(policy, formStep) {
  const steps = getAttendancePolicyWizardSteps(policy);
  const idx = steps.findIndex((s) => s.formStep === formStep);
  if (idx < 0 || idx >= steps.length - 1) return null;
  return steps[idx + 1].formStep;
}

export function getPrevAttendancePolicyFormStep(policy, formStep) {
  const steps = getAttendancePolicyWizardSteps(policy);
  const idx = steps.findIndex((s) => s.formStep === formStep);
  if (idx <= 0) return steps[0]?.formStep ?? 1;
  return steps[idx - 1].formStep;
}

export function getAttendancePolicyNextButtonLabel(policy, formStep) {
  const nextForm = getNextAttendancePolicyFormStep(policy, formStep);
  if (nextForm == null) return "Preview & save";
  const steps = getAttendancePolicyWizardSteps(policy);
  const currentId = getAttendancePolicyWizardCurrentId(policy, formStep);
  const next = steps.find((s) => s.id === currentId + 1);
  return `Next: ${next?.shortLabel || "Continue"}`;
}

/** Shift — timing template only (no org penalties / lock). */
export const ATTENDANCE_SHIFT_STEPS = [
  { id: 1, label: "Shift basics", shortLabel: "Basics" },
  { id: 2, label: "Timing & breaks", shortLabel: "Timing" },
  { id: 3, label: "Flexi, rotation & override", shortLabel: "Advanced" },
];

export const ONBOARDING_POLICY_STEPS = [
  { id: 1, label: "Policy basics", shortLabel: "Basics" },
  { id: 2, label: "Onboarding rules", shortLabel: "Rules" },
  { id: 3, label: "Workflow", shortLabel: "Workflow" },
  { id: 4, label: "Communication & letters", shortLabel: "Letters" },
  { id: 5, label: "Checklist mapping", shortLabel: "Checklist" },
  { id: 6, label: "Applicability", shortLabel: "Applicability" },
];

export const LOAN_POLICY_STEPS = [
  { id: 1, label: "Policy basics", shortLabel: "Basics" },
  { id: 2, label: "Eligibility & limits", shortLabel: "Limits" },
  { id: 3, label: "Interest, fees & EMI", shortLabel: "Interest" },
  { id: 4, label: "Recovery & tax", shortLabel: "Recovery" },
  { id: 5, label: "Workflow", shortLabel: "Workflow" },
  { id: 6, label: "Applicability", shortLabel: "Applicability" },
];

export const LEAVE_ENCASHMENT_POLICY_STEPS = [
  { id: 1, label: "Policy basics", shortLabel: "Basics" },
  { id: 2, label: "Encashment rules", shortLabel: "Rules" },
  { id: 3, label: "Pay, tax & mapping", shortLabel: "Pay" },
  { id: 4, label: "Workflow", shortLabel: "Workflow" },
  { id: 5, label: "Applicability", shortLabel: "Applicability" },
];

export const ONBOARDING_WORKFLOW_TABS = [
  { id: "approval", label: "New request", key: "workflowSteps", chainLabel: "onboarding request" },
  { id: "cancellation", label: "Cancellation", key: "cancellationWorkflowSteps", chainLabel: "cancellation request" },
];
